import { Box, IconButton } from "@mui/material";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useGetUserGroupHierarchyQuery, useGetUserRolesQuery } from "../../redux/reducers/rolesPermissions.reducer";
import { AGDataGrid } from "../../components/AGDataGrid";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ROWS_PER_PAGE } from "../../utils/constants";
import { InfoOutlined } from "@mui/icons-material";
import HierarchyDetailsDialog from "./HierarchyDetailsDialog";
import { useAppSelector } from "../../redux";

export interface IUserRoleHierarchyChildDetails {
	id: number;
	name: string;
}

export interface IUserRoleHierarchyRowDetails {
	id: number;
	sr_no: number;
	role_name: string;
	hierarchy_id: number;
	children: IUserRoleHierarchyChildDetails[];
}

const RolesHierarchy: FC = () => {
	const authState = useAppSelector((state) => state.auth);

	const [hierarchyDetailsDialogOpen, setHierarchyDetailsDialogOpen] = useState<boolean>(false);
	const [selectedHierarchyDetails, setSelectedHierarchyDetails] = useState<IUserRoleHierarchyRowDetails | null>(null);

	// APIS
	// GET USER ROLES
	const {
		data: getUserRolesResponse,
		isLoading: getUserRolesLoading,
		isFetching: getUserRolesFetching,
		refetch: refetchUserRoles
	} = useGetUserRolesQuery({});

	// GET USER GROUP HIERARCHY
	const {
		data: getUserGroupHierarchyResponse,
		isLoading: getUserGroupHierarchyLoading,
		isFetching: getUserGroupHierarchyFetching,
		refetch: refetchUserGroupHierarchy
	} = useGetUserGroupHierarchyQuery();

	const readOnlyPermission = useMemo<boolean>(
		() => !authState.user.permissions || !authState.user.permissions.user_management?.edit,
		[authState.user.permissions]
	);

	const rowsData = useMemo<IUserRoleHierarchyRowDetails[]>(() => {
		if (!getUserRolesResponse || !getUserGroupHierarchyResponse) return [];

		return getUserRolesResponse.map((userRole, index) => {
			const hierarchyNode = getUserGroupHierarchyResponse.find((item) => item.group.id === userRole.id);

			return {
				id: userRole.id,
				sr_no: index + 1,
				role_name: userRole.name,
				hierarchy_id: hierarchyNode?.id ?? 0,
				children: hierarchyNode?.children ?? []
			};
		});
	}, [getUserGroupHierarchyResponse, getUserRolesResponse]);

	const refetchAllData = useCallback(() => {
		refetchUserRoles();
		refetchUserGroupHierarchy();
	}, [refetchUserGroupHierarchy, refetchUserRoles]);

	function handleOpenHierarchyDetailsDialog(hierarchyDetails: IUserRoleHierarchyRowDetails) {
		setSelectedHierarchyDetails(hierarchyDetails);
		setHierarchyDetailsDialogOpen(true);
	}

	function handleCloseHierarchyDetailsDialog() {
		setHierarchyDetailsDialogOpen(false);
		setSelectedHierarchyDetails(null);
		refetchAllData();
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 65,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "role_name",
			headerName: "Role Name",
			flex: 1,
			minWidth: 120,
			sortable: false
		},
		{
			field: "children",
			headerName: "Lower-level Roles",
			flex: 2,
			minWidth: 200,
			sortable: false,
			valueFormatter: (value: IUserRoleHierarchyChildDetails[]) => value.map((item) => item.name).join(", ")
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IUserRoleHierarchyRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton color="info" size="small" onClick={() => handleOpenHierarchyDetailsDialog(params.row)}>
						<InfoOutlined fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="page-content-wrapper">
			<Box className="page-content">
				<AGDataGrid
					hideLastSeparator
					columns={columnsData}
					rows={rowsData}
					paginationMode="client"
					loading={
						getUserRolesLoading || getUserRolesFetching || getUserGroupHierarchyLoading || getUserGroupHierarchyFetching
					}
					hideFooter={(getUserRolesResponse?.length ?? 0) <= ROWS_PER_PAGE}
					columnVisibilityModel={{ actions: !readOnlyPermission }}
				/>
			</Box>

			<HierarchyDetailsDialog
				open={hierarchyDetailsDialogOpen}
				hierarchyDetails={selectedHierarchyDetails}
				onClose={handleCloseHierarchyDetailsDialog}
			/>
		</Box>
	);
};

export default RolesHierarchy;
